import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import MobileMenu from "../../components/ui/mobile_menu2";
import { getTranslatedCategoryName } from "../../utils/categoryTranslation";
import {
  Search, Globe, X, MessageSquare, Mail,
  HelpCircle, AlertCircle, Users, Star,
  Shield, Heart, Info, ChevronDown,
  Languages, Filter, MapPin, MessageCircle,
  Scale, Building, FileText, ThumbsUp,
  Briefcase, Check, User, BadgeCheck,
  Clock, Calendar, Zap, Send, Phone, Package, Home
} from "lucide-react";
import PolicyModals from "./Policy";
import SharedFooter3 from "../../Footer/SharedFooter3";
import MobileMenu2 from "../../modules/shared/components/MobileMenu";
import FavoriteButton from "../../components/ui/FavoriteButton";
import { useSelector } from "react-redux";
import { useAppDispatch, LogoutUser, LogoutManually, getTypeCategories, getInternationalProfessionals } from "../../redux";
import MyProfile from "../../modules/professional/components/MyProfile";
import { TypeEnum } from "../../modules/shared/model/type.enum";
import { RequestQuoteModal } from "../../components/ui";
import ReviewModal from "../../components/ui/ReviewModal";
import { RoleEnum } from "../../modules";
import { useNavigate } from "react-router-dom";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import LanguageSelector from "../../components/LanguageSelector";

// Random icons array for categories
const randomIcons = [
  AlertCircle, Shield, FileText, Heart, Users, Star,
  HelpCircle, Clock, Phone, Mail, MapPin, Globe,
  Check, Zap, Package, Home, User, MessageCircle,
  Scale, Building, Briefcase, Languages
];

// Chat Modal Component
// Chat Modal Component
const ChatModal = ({ isOpen, onClose, professional, selectedType, onLogin }) => {
  const [messages, setMessages] = useState(["Hello, I require more information"]); // Array for message history
  const [currentMessage, setCurrentMessage] = useState(""); // Current input

  if (!isOpen) return null;

  const handleSend = () => {
    // if (currentMessage.trim()) {
    setMessages([...messages, currentMessage]); // Add new message to history
    setCurrentMessage(""); // Clear input
    onLogin(); // Trigger login as per original behavior
    // }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-xl max-w-2xl w-full h-[600px] flex flex-col shadow-lg"
        >
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-semibold">Chat about {professional?.service || "Service"}</h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <div key={index} className="bg-gray-100 rounded-lg p-3 max-w-[80%]">
                  <p className="text-sm">{msg}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 border-t">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Type your message..."
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                className="flex-1 border rounded-lg px-4 py-2"
              />
              <button
                onClick={handleSend}
                className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Login Registration Popup Component
// Login Registration Popup Component
const LoginRegistrationPopup = ({ isOpen, onClose }) => {
  const handleContinueWithEmail = () => {
    window.location.href = "/register";
    onClose();
  };

  const handleLogin = () => {
    window.location.href = "/auth/login";
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-[20px] max-w-[90%] sm:max-w-md w-full shadow-lg"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="flex items-center">
              <img
                src="https://assests.netlify.app/assets/images/logo.png"
                alt="Kokobeo"
                className="h-6 sm:h-8"
                onError={(e: any) => e.target.src = "/fallback-logo.png"}
              />
              <span className="ml-2 text-blue-600 text-lg sm:text-xl font-bold">Kokobeo</span>
            </div>
            <button
              onClick={onClose}
              className="p-1 sm:p-2 hover:bg-gray-100 rounded-full bg-red-500 text-white"
              aria-label="Close popup"
            >
              <X className="h-3 sm:h-5 w-3 sm:w-5" />
            </button>
          </div>

          <h3 className="text-base sm:text-xl font-semibold mb-2 sm:mb-4" style={{ textAlign: "center" }}>Create an Account</h3>
          <p className="text-gray-600 text-sm sm:text-base mb-2 sm:mb-4" style={{ textAlign: "center" }}>I want to...</p>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4 sm:mb-6">
            <button
              onClick={() => console.log("Hire Professional clicked")}
              className="flex-1 bg-white border border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center hover:bg-gray-50 min-h-[100px] transition-colors"
            >
              <User className="h-6 w-6 text-gray-500 mb-2" />
              <span className="text-sm text-gray-700 text-center">Hire Professional</span>
            </button>
            <button
              onClick={() => console.log("Work as Professional clicked")}
              className="flex-1 bg-white border border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center hover:bg-gray-50 min-h-[100px] transition-colors"
            >
              <User className="h-6 w-6 text-gray-500 mb-2" />
              <span className="text-sm text-gray-700 text-center">Work as Professional</span>
            </button>
          </div>

          <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-4">
            {[
              { src: "https://cdn1.iconfinder.com/data/icons/google-s-logo/150/Google_Icons-09-512.png", alt: "Google", text: "Google" },
              { src: "https://www.shutterstock.com/image-vector/batumi-georgia-december-1-2023-600nw-2395541231.jpg", alt: "Apple", text: "Apple" },
              { src: "https://cdn2.iconfinder.com/data/icons/social-media-2210/24/Facebook-512.png", alt: "Facebook", text: "Facebook" },
              { src: "https://cdn3.iconfinder.com/data/icons/social-media-2210/24/Instagram-512.png", alt: "Instagram", text: "Instagram" },
            ].map((btn, index) => (
              <button
                key={index}
                className="w-[48%] sm:flex-1 bg-white border border-gray-300 rounded-lg p-1 sm:p-2 flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <img src={btn.src} alt={btn.alt} className="h-4 sm:h-5 w-4 sm:w-5 mr-1 sm:mr-2" />
                {btn.text}
              </button>
            ))}
          </div>

          <p className="text-gray-500 text-xs sm:text-sm mb-2 sm:mb-4 text-center font-medium">or</p>

          <button
            onClick={handleContinueWithEmail}
            className="w-full bg-blue-600 text-white py-1.5 sm:py-2 rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm"
          >
            Continue with Email
          </button>

          <p className="text-sm text-gray-600 mt-2 text-center text-xs sm:text-sm">
            Already have an account?{" "}
            <button
              onClick={handleLogin}
              className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm transition-colors"
            >
              Log in
            </button>
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Professional Card Component
const ProfessionalCard = ({ pro, onViewProfile }) => {
  const [showChatModal, setShowChatModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const { user } = useSelector((state: any) => state.auth);
  const navigate = useNavigate();
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [visibleSubCounts, setVisibleSubCounts] = useState<Record<number, number>>({});

  const handleShowLogin = () => {
    setShowLoginPopup(true);
  };

  return (
    <div className="border rounded-xl p-6 hover:shadow-lg transition-shadow">
      <div className="flex gap-4">
        <div className="flex-shrink-0">
          {pro.photo ? (
            <img
              src={pro.photo}
              alt={`${pro.name}'s profile`}
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-gray-400" />
            </div>
          )}
        </div>

        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">{pro.name}</h3>
            <Star className="h-5 w-5 text-yellow-400 fill-current" />
            <span className="font-medium">{pro.rating}</span>
            <span className="text-gray-500">({pro.reviewsCount})</span>
          </div>
          <p className="text-blue-600">{pro.service}</p>
          <button
            onClick={() => setShowReviewModal(true)}
            className="mt-1 px-3 py-1 bg-blue-100 text-blue-600 rounded-md text-sm hover:bg-blue-200 w-fit"
          >
            See all reviews
          </button>
          <button
            onClick={() => onViewProfile(pro)}
            className="mt-1 px-3 py-1 bg-green-100 text-green-600 rounded-md text-sm hover:bg-green-200 w-fit"
          >
            View Profile
          </button>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div className="flex items-center text-gray-600">
          <MapPin className="h-4 w-4 mr-2" />
          <div className="ml-0 flex flex-wrap gap-1">
            {pro.location && typeof pro.location === "string" ? (
              <span className="text-gray-700">{pro.location}</span>
            ) : Array.isArray(pro.location) && pro.location.length > 0 ? (
              pro.location.map((loc: any, index: number) => (
                <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {loc.cities && loc.cities.length >0 && loc.cities.join(", ")}{loc.province ? `, ${loc.province}` : ""}
                </span>
              ))
            ) : (
              <span className="text-gray-500">No location</span>
            )}
          </div>
        </div>
        {pro.timeZone && (
          <div className="flex items-center text-gray-600">
            <Clock className="h-4 w-4 mr-2" />
            {pro.timeZone}
          </div>
        )}
        <div className="flex items-center text-gray-600">
          <Globe className="h-4 w-4 mr-2" />
          {pro.languages.join(", ")}
        </div>
        <p className="text-gray-600">{pro.description}</p>

        <div className="flex gap-3 mb-4">
          {pro.verified && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <Shield className="w-3 h-3 mr-1" />
              Background Checked
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {pro.expertise.map((skill, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
            >
              {skill}
            </span>
          ))}
        </div>

        {Array.isArray(pro.categoriesDetailed) && pro.categoriesDetailed.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Categories & Specializations</h4>
            <div className="space-y-2">
              {pro.categoriesDetailed.map((cat, idx) => (
                <div key={idx} className="text-sm">
                  <div className="inline-block px-2 py-0.5 rounded bg-green-50 text-green-700 border border-green-200 mr-2 mb-1">
                    {cat.name}
                  </div>
                  {Array.isArray(cat.subcategories) && cat.subcategories.length > 0 && (
                    <div className="flex flex-wrap gap-1 ml-2 mt-1">
                      {(cat.subcategories.slice(0, visibleSubCounts[idx] ?? 3)).map((sub, sIdx) => (
                        <span key={sIdx} className="px-2 py-0.5 rounded bg-gray-50 text-gray-700 border border-gray-200">
                          {sub}
                        </span>
                      ))}
                      {cat.subcategories.length > (visibleSubCounts[idx] ?? 3) && (
                        <button
                          type="button"
                          onClick={() => setVisibleSubCounts(prev => ({ ...prev, [idx]: (prev[idx] ?? 3) + 3 }))}
                          className="px-2 py-0.5 text-xs text-blue-600 hover:underline"
                        >
                          Show more
                        </button>
                      )}
                      {(visibleSubCounts[idx] ?? 3) > 3 && (
                        <button
                          type="button"
                          onClick={() => setVisibleSubCounts(prev => ({ ...prev, [idx]: 3 }))}
                          className="px-2 py-0.5 text-xs text-blue-600 hover:underline ml-1"
                        >
                          See less
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm mt-4">
          <div>
            <span className="text-gray-500">Price:</span>
            <span className="ml-2 font-medium">{pro.price}</span>
          </div>
          <div>
            <span className="text-gray-500">Available:</span>
            <span className="ml-2 font-medium">{pro.availability}</span>
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={() => {
              if (!user?.id) {
                setShowChatModal(true);
                return;
              }
              if (Number(user?.role?.id) === RoleEnum.client) {
                setShowQuoteModal(true);
                return;
              }
              setShowChatModal(true);
            }}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Contact Now
          </button>
          <button
            onClick={() => {
              if (!user?.id) {
                setShowChatModal(true);
                return;
              }
              if (Number(user?.role?.id) === RoleEnum.client) {
                setShowQuoteModal(true);
                return;
              }
              setShowChatModal(true);
            }}
            className="flex items-center justify-center p-2 border rounded-lg hover:bg-gray-50"
          >
            <MessageCircle className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        <ChatModal
          isOpen={showChatModal}
          onClose={() => setShowChatModal(false)}
          professional={pro}
          selectedType="hire"
          onLogin={() => { window.location.href = "/auth/register"; }}
        />
        {showQuoteModal && (
          <RequestQuoteModal
            isOpen={true}
            onClose={() => setShowQuoteModal(false)}
            professional={{ id: pro.id, name: pro.name }}
            isProfessional={false}
            onOfferCreated={(offer: any) => {
              console.log("offer123", offer);
              setShowQuoteModal(false);
              window.location.href = "/client/MessageAndVideoCall?professionalId=" + offer.user.id + "&professionalName=" + encodeURIComponent(offer.user.firstName + " " + offer.user.lastName) + "&offerId=" + offer.id;
              // navigate(`/client/MessageAndVideoCall?professionalId=${offer.user.id}&professionalName=${encodeURIComponent(offer.user.firstName + " " + offer.user.lastName)}&offerId=${offer.id}`);
            }}
          />
        )}
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          professional={pro.origionalProfessional}
        />
        <LoginRegistrationPopup
          isOpen={showLoginPopup}
          onClose={() => setShowLoginPopup(false)}
        />
      </AnimatePresence>
    </div>
  );
};

// Service Page Component
const ServicePage = ({ service, onClose, selectedCategoryId }) => {
  const { t } = useTranslation();
  const [priceRange, setPriceRange] = useState([0, 500]); // [min, max]
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(8);
  const [showEmergencyOnly, setShowEmergencyOnly] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [selectedProfileProfessional, setSelectedProfileProfessional] = useState(null);

  const dispatch = useAppDispatch();
  const internationalProfessionalsState = useSelector((state: any) => state.admin.internationalProfessionals);
  const { data: rawProfessionals = [], totalCount = 0, hasNextPage = false, currentPage: storeCurrentPage = 1, totalPages = 1 } = internationalProfessionalsState || {};
  const isLoading = useSelector((state: any) => state.loader.isLoading);

  // Map backend data to expected format
  const professionals = rawProfessionals.map((professional, index) => {

    // Get categories and subcategories
    const categories = professional.categories && professional.categories.length > 0
      ? professional.categories.map(cat => cat.name).filter(Boolean)
      : [];
    const categoriesDetailed = professional.categories && professional.categories.length > 0
      ? professional.categories.map(cat => ({
        name: cat.name,
        subcategories: Array.isArray(cat.subcategories) ? cat.subcategories : []
      }))
      : [];

    // Get skills
    const skills = professional.skills && professional.skills.length > 0
      ? professional.skills.map(skill => skill.name || skill).filter(Boolean)
      : [];

    return {
      id: professional.id || index + 1,
      name: professional.name || "Professional",
      photo: professional.photo?.path,
      service: professional.title || "Professional Service",
      rating: professional.averageRating || 0,
      reviews: professional.reviewsList || [],
      reviewsCount: professional.reviewsCount || 0,
      location: (professional.location && professional.location.length ? professional.location : (professional.country || "No location")),
      languages: professional.languages || ["English"],
      description: professional.bio || professional.description || "Professional service provider",
      price: professional.hourlyRate ? `$${professional.hourlyRate}/hour` : "$80/hour",
      expertise: skills,
      availability: professional.availability || "Flexible",
      verified: professional.verified || false,
      categoriesDetailed: categoriesDetailed,
      timeZone: professional.timeZone || professional.timezone || undefined,
      origionalProfessional: professional
    };
  });

  // Fetch professionals when component mounts or category changes
  useEffect(() => {
    if (selectedCategoryId) {
      // Reset filters when category changes
      setPriceRange([0, 500]);
      setShowEmergencyOnly(false);
      setSelectedLanguages([]);
      setCurrentPage(1);
      
      // Initial fetch with just category
      dispatch(getInternationalProfessionals({
        page: 1,
        limit: limit,
        categoryId: selectedCategoryId
      }));
    }
  }, [dispatch, selectedCategoryId, limit]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Fetch professionals with current filters when page changes
    dispatch(getInternationalProfessionals({
      page: page,
      limit: limit,
      categoryId: selectedCategoryId,
      minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
      maxHourlyRate: priceRange[1] > 0 ? priceRange[1] : undefined,
      isEmergency: showEmergencyOnly ? true : undefined,
      languages: selectedLanguages.length > 0 ? selectedLanguages.join(",") : undefined
    }));
  };

  // Handle filter changes - only called when Apply Filters button is clicked
  const handleFilterChange = () => {
    setCurrentPage(1); // Reset to first page when filters change
    // Trigger a new search with current filters
    if (selectedCategoryId) {
      dispatch(getInternationalProfessionals({
        page: 1,
        limit: limit,
        categoryId: selectedCategoryId,
        minHourlyRate: priceRange[0] > 0 ? priceRange[0] : undefined,
        maxHourlyRate: priceRange[1] > 0 ? priceRange[1] : undefined,
        isEmergency: showEmergencyOnly ? true : undefined,
        languages: selectedLanguages.length > 0 ? selectedLanguages.join(",") : undefined
      }));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 overflow-y-auto"
    >
      <div className="min-h-screen px-4 py-8" style={{ textAlign: "left" }}>
        <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg">
          <div className="p-6 border-b flex justify-between items-center bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-t-xl">
            <div>
              <h2 className="text-2xl font-semibold">{service}</h2>
              <p className="text-sm text-blue-100">{t("internationalHomepage.servicePage.subtitle")}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="md:hidden p-4 border-b">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="w-full flex items-center justify-center gap-2 bg-gray-100 py-2 px-4 rounded-lg text-gray-700"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? t("internationalHomepage.servicePage.hideFilters") : t("internationalHomepage.servicePage.showFilters")}
            </button>
          </div>

          <div className={`p-4 sm:p-6 border-b bg-gray-50 ${showFilters ? "block" : "hidden md:block"}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t("internationalHomepage.servicePage.filters.priceRange")}</label>
                <div className="space-y-2">
                  <input
                    type="number"
                    placeholder={t("internationalHomepage.servicePage.filters.minPrice")}
                    value={priceRange[0] || ""}
                    onChange={(e) => setPriceRange([Number(e.target.value), priceRange[1]])}
                    className="w-full rounded-lg border-gray-300 shadow-sm p-2"
                  />
                  <input
                    type="number"
                    placeholder={t("internationalHomepage.servicePage.filters.maxPrice")}
                    value={priceRange[1] || ""}
                    onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                    className="w-full rounded-lg border-gray-300 shadow-sm p-2"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t("internationalHomepage.servicePage.filters.emergencyOnly")}</label>
                <button
                  onClick={() => setShowEmergencyOnly(!showEmergencyOnly)}
                  className={`w-full py-2 px-4 rounded-lg border ${showEmergencyOnly ? "bg-red-50 border-red-200 text-red-700" : "bg-white border-gray-300 text-gray-700"}`}
                >
                  {showEmergencyOnly ? t("internationalHomepage.servicePage.filters.emergencyOnlyActive") : t("internationalHomepage.servicePage.filters.allServices")}
                </button>
                <button
                  onClick={handleFilterChange}
                  disabled={isLoading}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                >
                  {isLoading ? t("internationalHomepage.servicePage.filters.applying") : t("internationalHomepage.servicePage.filters.applyFilters")}
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 border-b bg-blue-50">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{totalCount || 0}</div>
                <div className="text-sm text-gray-600">{t("internationalHomepage.servicePage.stats.availableProfessionals")}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {professionals && professionals.length > 0
                    ? (professionals.reduce((sum, prof) => sum + (prof.rating || 0), 0) / professionals.length).toFixed(1)
                    : "0.0"
                  }
                </div>
                <div className="text-sm text-gray-600">{t("internationalHomepage.servicePage.stats.averageRating")}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">30min</div>
                <div className="text-sm text-gray-600">{t("internationalHomepage.servicePage.stats.avgResponseTime")}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {professionals && professionals.length > 0
                    ? Math.round((professionals.filter(prof => prof.verified).length / professionals.length) * 100)
                    : 0
                  }%
                </div>
                <div className="text-sm text-gray-600">{t("internationalHomepage.servicePage.stats.verifiedProfessionals")}</div>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 gap-6">
              <AnimatePresence mode="wait">
                {!isLoading && professionals && professionals.length > 0 ? (
                  professionals.map((pro, index) => (
                    <motion.div
                      key={pro.id}
                      initial={{ opacity: 0, x: -30, scale: 0.95 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      transition={{ 
                        delay: index * 0.08,
                        duration: 0.4,
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }}
                    >
                      <ProfessionalCard
                        pro={pro}
                        onViewProfile={(professional) => { setSelectedProfileProfessional(professional); setShowProfileDialog(true); }}
                      />
                    </motion.div>
                  ))
                ) : isLoading ? (
                  // Loading skeleton - show while loading
                  Array.from({ length: 6 }).map((_, index) => (
                    <motion.div 
                      key={`skeleton-${index}`}
                      initial={{ opacity: 0, y: 20 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ delay: index * 0.05, duration: 0.3 }} 
                      className="bg-gradient-to-br from-white to-gray-50 rounded-lg h-24 sm:h-[120px] shadow-[0_2px_8px_rgba(0,0,0,0.05)] overflow-hidden relative"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-shimmer"></div>
                      <div className="w-full h-full flex flex-col items-center justify-center p-4">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-300 rounded mb-2 animate-pulse"></div>
                        <div className="w-16 h-3 bg-gray-300 rounded animate-pulse"></div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-10">
                    <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">{t("internationalHomepage.servicePage.noProfessionalsFound")}</p>
                    <p className="text-gray-400 text-sm">{t("internationalHomepage.servicePage.tryAdjusting")}</p>
                    <button
                      onClick={() => handleFilterChange()}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-2"
                    >
                      {t("internationalHomepage.servicePage.filters.applyFilters")}
                    </button>
                  </div>
                )}
              </AnimatePresence>
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex justify-center items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-gray-600 text-sm disabled:opacity-50"
                  disabled={currentPage === 1}
                >
                  {t("internationalHomepage.servicePage.pagination.previous")}
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => handlePageChange(i + 1)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-50 ${currentPage === i + 1 ? "bg-blue-50 text-blue-600 font-medium border-blue-200 border" : "text-gray-600"}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-gray-600 text-sm disabled:opacity-50"
                  disabled={!hasNextPage}
                >
                  {t("internationalHomepage.servicePage.pagination.next")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showReviewModal && (
          <ReviewModal
            isOpen={showReviewModal}
            onClose={() => setShowReviewModal(false)}
            professional={selectedProfileProfessional.origionalProfessional}
          />
        )}
        {showProfileDialog && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={() => setShowProfileDialog(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-4 z-50 overflow-hidden"
            >
              <div className="h-full bg-white rounded-lg shadow-xl flex flex-col">
                {/* Dialog Header */}
                <div className="flex items-center justify-between p-4 border-b">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedProfileProfessional?.name}&apos;s Profile
                  </h2>
                  <button
                    onClick={() => setShowProfileDialog(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 w-5 text-gray-500" />
                  </button>
                </div>

                {/* Dialog Content */}
                <div className="flex-1 overflow-y-auto p-4">
                  {selectedProfileProfessional && (
                    <MyProfile
                      isAdminEdit={true}
                      isViewing={true}
                      adminUserId={selectedProfileProfessional.id}
                      onUserUpdated={() => {
                        // Refresh the professionals list if needed
                        // handleSearch();
                      }}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ServiceOptionsModal Component
const ServiceOptionsModal = ({ isOpen, onClose, service }) => {
  const [activeTab, setActiveTab] = useState("emergency");
  const [requestType, setRequestType] = useState("direct");

  if (!isOpen) return null;

  const tabs = [
    { id: "emergency", label: "Emergency", className: "text-red-600 bg-red-50" },
    { id: "hiring", label: "Available for Hiring", className: "text-green-600 bg-green-50" },
    { id: "info", label: "Customer Choice: Information Only", className: "text-purple-600 bg-purple-50" }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-xl w-full max-w-2xl"
      >
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">{service}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex gap-2 p-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 rounded-full text-sm ${tab.className} ${activeTab === tab.id ? "ring-2 ring-offset-2" : ""
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "emergency" && (
          <div className="mx-4 p-4 bg-red-50 rounded-lg border border-red-100 mb-4">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p>Emergency Service: Client agreed to pay {getDynamicCurrencySymbol()}80 outgoing fee</p>
            </div>
          </div>
        )}

        <div className="p-4">
          <h3 className="text-base font-semibold mb-4">Request Type</h3>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setRequestType("quote")}
              className={`p-3 rounded-lg border text-center ${requestType === "quote" ? "bg-blue-50 border-blue-200" : "border-gray-200"
                }`}
            >
              Request Quote
            </button>
            <button
              onClick={() => setRequestType("direct")}
              className={`p-3 rounded-lg border text-center ${requestType === "direct" ? "bg-blue-600 text-white" : "border-gray-200"
                }`}
            >
              Direct Hiring
            </button>
          </div>
        </div>

        <div className="px-4 pb-4">
          <h3 className="text-base font-semibold mb-3">Direct hiring includes:</h3>
          <div className="grid gap-3">
            <div className="flex items-center gap-2 text-gray-700">
              <MessageSquare className="h-5 w-5 text-blue-500" />
              <span>Immediate chat with Customer</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Clock className="h-5 w-5 text-blue-500" />
              <span>Real-time service coordination</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Calendar className="h-5 w-5 text-blue-500" />
              <span>Direct scheduling</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Zap className="h-5 w-5 text-blue-500" />
              <span>Instant professional assistance</span>
            </div>
          </div>
        </div>

        <div className="px-4 pb-4">
          <h3 className="text-base font-semibold mb-3">Service Location</h3>
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Floor Level</label>
                <select className="w-full p-2 border rounded-lg">
                  <option>Ground Floor</option>
                </select>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">No Elevator</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="rounded" />
                  <span className="text-sm">No Stairs</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Address</label>
                <input
                  type="text"
                  value="*** Queen Street"
                  readOnly
                  className="w-full p-2 border rounded-lg bg-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Building Type</label>
                <input
                  type="text"
                  value="Residential"
                  readOnly
                  className="w-full p-2 border rounded-lg bg-white"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 pb-6">
          <h3 className="text-base font-semibold mb-3">Request Details</h3>
          <textarea
            className="w-full p-3 border rounded-lg h-24"
            defaultValue="Major water leak in master bathroom requiring immediate attention."
            readOnly
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

// InfoPopup Component
const InfoPopup = ({ isOpen, onClose, title, content }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-xl max-w-lg w-full p-6 relative"
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full"
        >
          <X className="h-5 w-5" />
        </button>
        <h3 className="text-xl font-semibold mb-4">{title}</h3>
        <div className="prose max-w-none">
          {content}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Welcome Popup Component
const WelcomePopup = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  useEffect(() => {
    const hasSeenPopup = localStorage.getItem("hasSeenInternationalWelcomePopup");
    if (!hasSeenPopup && isOpen) {
      const timer = setTimeout(() => {
        onClose();
        localStorage.setItem("hasSeenInternationalWelcomePopup", "true");
      }, 4000);
      return () => clearTimeout(timer);
    } else if (hasSeenPopup) {
      onClose();
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed bottom-[15%] left-4 right-4 sm:left-1/2 sm:-translate-x-1/2 sm:max-w-md bg-white p-4 sm:p-6 rounded-xl shadow-lg z-50"
    >
      <h3 className="text-xl font-semibold mb-2">{t("internationalHomepage.welcomePopup.title")}</h3>
      <p className="text-gray-600">
        {t("internationalHomepage.welcomePopup.subtitle")}
      </p>
    </motion.div>
  );
};

// Main Component
const ProHomepage = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split("-")[0];
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(!localStorage.getItem("hasSeenInternationalWelcomePopup"));
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showProfessionalTermsModal, setShowProfessionalTermsModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [showHiring, setShowHiring] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated, rehydrated, user } = useSelector((state: any) => state.auth);
  const { typeCategories } = useSelector((state: any) => state.client);
  const dispatch = useAppDispatch();

  // Get random icon for categories
  const getRandomIcon = () => {
    return randomIcons[Math.floor(Math.random() * randomIcons.length)];
  };

  // Transform API categories to display format
  const transformCategories = (categories) => {
    if (!categories || !Array.isArray(categories)) return [];

    return categories.slice(0, 6).map((category, index) => ({
      ...category,
      icon: getRandomIcon()
    }));
  };

  const transformAllCategories = (categories) => {
    if (!categories || !Array.isArray(categories)) return [];

    return categories.map((category, index) => ({
      ...category,
      icon: getRandomIcon()
    }));
  };

  // Filter categories based on search query (including translated names)
  const filterCategories = (categories, query) => {
    if (!query.trim()) return categories;
    
    const lowerQuery = query.toLowerCase();
    return categories.filter(category => {
      const englishName = category.name?.toLowerCase() || "";
      const translatedName = getTranslatedCategoryName(category, currentLang)?.toLowerCase() || "";
      return englishName.includes(lowerQuery) || translatedName.includes(lowerQuery);
    });
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        await dispatch(getTypeCategories({
          id: TypeEnum.International_Service
        }));
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length > 1) {
        const transformedCategories = transformAllCategories(typeCategories);
        const filtered = filterCategories(transformedCategories, searchQuery);
        setFilteredCategories(filtered);
      }
      else {
        const transformedCategories = transformCategories(typeCategories);
        const filtered = filterCategories(transformedCategories, searchQuery);
        setFilteredCategories(filtered);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, typeCategories]);

  const handleLogout = async () => {
    try {
      const response = await dispatch(LogoutUser({})).unwrap();
    } catch (error: any) {
      if (error === 401) {
        dispatch(LogoutManually());
        console.log("Token expired");
      } else {
        console.log("Logout failed:", error);
      }
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <header className="fixed w-full bg-white shadow-sm z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={() => setShowMobileMenu(true)}
                className="flex items-center text-gray-700"
              >
                <span className="hidden sm:inline mr-2">{t("internationalHomepage.header.menu")}</span>
                <span className="w-5 h-5 sm:w-6 sm:h-6">☰</span>
              </button>

              <a href="/internationalhompage" className="flex items-center no-underline">
                <img
                  src="https://assests.netlify.app/assets/images/logo.png"
                  alt="Kokobeo"
                  className="h-6 sm:h-8 w-auto"
                />
                <span className="ml-2 text-blue-600 text-lg sm:text-xl font-bold">
                  Kokobeo
                </span>
              </a>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              {/* Language Selector */}
              <LanguageSelector variant="modal" />
              
              <a
                href="/international-professionals"
                className="bg-blue-600 text-white px-2 sm:px-4 py-1.5 sm:py-2 rounded-md hover:bg-blue-700 transition-colors no-underline text-xs sm:text-sm whitespace-nowrap"
              >
                <span className="block sm:hidden">{t("internationalHomepage.header.joinAsPro")}</span>
                <span className="hidden sm:block">{t("internationalHomepage.header.areYouProfessional")}</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      <MobileMenu2
        isOpen={showMobileMenu}
        onClose={() => setShowMobileMenu(false)}
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
        currentUser={user} />

      <main className="min-h-screen pt-20 sm:pt-24 pb-24 flex flex-col items-center justify-center px-3 sm:px-4">
        <div className="w-full max-w-[800px] mx-auto">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gray-900 mb-6">
            {t("internationalHomepage.main.title")}
          </h1>
          <p className="text-xl text-gray-600 text-center mb-8">
            {t("internationalHomepage.main.subtitle")}
          </p>

          <div className="relative w-full mb-12">
            <input
              type="text"
              placeholder={isLoading ? t("internationalHomepage.main.loadingCategories") : t("internationalHomepage.main.searchPlaceholder")}
              value={searchQuery}
              onChange={handleSearchChange}
              disabled={isLoading}
              className="w-full h-14 pl-12 pr-4 rounded-full border border-gray-200 
                focus:border-blue-500 focus:ring-2 focus:ring-blue-100 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <Search
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={24}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {isLoading || !typeCategories || typeCategories.length === 0 ? (
              // Loading skeleton - show while loading OR if no categories from API
              Array.from({ length: 6 }).map((_, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-white rounded-xl h-24 shadow-sm animate-pulse">
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <div className="w-8 h-8 bg-gray-300 rounded mb-3"></div>
                    <div className="w-16 h-3 bg-gray-300 rounded"></div>
                  </div>
                </motion.div>
              ))
            ) : filteredCategories.length > 0 ? (
              filteredCategories.map((category, index) => (
                <motion.button
                  key={index}
                  onClick={() => setSelectedService(category.id)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex flex-col items-center p-6 bg-white rounded-xl hover:shadow-md transition-shadow"
                >
                  <category.icon className="h-8 w-8 text-blue-600 mb-3" />
                  <p className="text-sm font-medium text-gray-900 text-center">
                    {getTranslatedCategoryName(category, currentLang)}
                  </p>
                </motion.button>
              ))
            ) : searchQuery ? (
              <div className="col-span-2 sm:col-span-3 md:col-span-6 text-center py-8">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">{t("internationalHomepage.main.noCategoriesFound", { query: searchQuery })}</p>
                <p className="text-gray-400 text-sm">{t("internationalHomepage.main.tryAdjusting")}</p>
                <button
                  onClick={() => setSearchQuery("")}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-2"
                >
                  {t("internationalHomepage.main.clearSearch")}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </main>
      <br></br>
      <br></br>
      <br></br>

      {!isLoading && <SharedFooter3 />}

      <AnimatePresence>
        {selectedService && (
          <ServicePage
            service={getTranslatedCategoryName(filteredCategories.find(cat => cat.id === selectedService), currentLang) || selectedService}
            onClose={() => setSelectedService(null)}
            selectedCategoryId={selectedService}
          />
        )}

        {showReviewModal && (
          <ReviewModal
            isOpen={showReviewModal}
            onClose={() => setShowReviewModal(false)}
            professional={selectedProfessional.origionalProfessional}
          />
        )}

        <InfoPopup
          isOpen={showHowItWorks}
          onClose={() => setShowHowItWorks(false)}
          title="How it Works"
          content={
            <div className="space-y-4" style={{ textAlign: "left" }}>
              <p>Find the perfect international professional:</p>
              <ul className="list-disc pl-4">
                <li>Search for experts in your required field</li>
                <li>Compare multiple service offerings</li>
                <li>Read verified reviews from clients worldwide</li>
                <li>Choose your preferred professional and get started</li>
              </ul>
            </div>
          }
        />

        <InfoPopup
          isOpen={showHiring}
          onClose={() => setShowHiring(false)}
          title="Hire International Professional"
          content={
            <div className="space-y-4" style={{ textAlign: "left" }}>
              <p>Direct hiring process:</p>
              <ul className="list-disc pl-4">
                <li>Browse professional profiles</li>
                <li>View certifications and expertise</li>
                <li>Contact professionals directly</li>
                <li>Discuss your requirements</li>
                <li>Agree on terms and begin collaboration</li>
              </ul>
            </div>
          }
        />

        <InfoPopup
          isOpen={showQuotes}
          onClose={() => setShowQuotes(false)}
          title="Get International Service Quotes"
          content={
            <div className="space-y-4" style={{ textAlign: "left" }}>
              <p>Receive quotes from international professionals:</p>
              <ul className="list-disc pl-4">
                <li>Submit your project details</li>
                <li>Receive multiple quotes</li>
                <li>Compare services and pricing</li>
                <li>Choose the best offer for your needs</li>
                <li>Get anonymous feedback options</li>
              </ul>
            </div>
          }
        />

        <InfoPopup
          isOpen={showReviews}
          onClose={() => setShowReviews(false)}
          title="International Service Reviews"
          content={
            <div className="space-y-4" style={{ textAlign: "left" }}>
              <p>Share your experience with international professionals:</p>
              <ul className="list-disc pl-4">
                <li>Rate your experience</li>
                <li>Provide detailed feedback</li>
                <li>Help others make informed decisions</li>
                <li>Build our global professional community</li>
              </ul>
            </div>
          }
        />
      </AnimatePresence>

      <WelcomePopup
        isOpen={showWelcomePopup}
        onClose={() => setShowWelcomePopup(false)}
      />

      <PolicyModals
        showPrivacyModal={showPrivacyModal}
        showTermsModal={showTermsModal}
        showProfessionalTermsModal={showProfessionalTermsModal}
        onClosePrivacy={() => setShowPrivacyModal(false)}
        onCloseTerms={() => setShowTermsModal(false)}
        onCloseProfessionalTerms={() => setShowProfessionalTermsModal(false)}
      />
    </div>
  );
};

export default ProHomepage;